// Pods: everything at this station today, done included.
//
// The queue only ever shows what is still to do, which answers "what next" and
// not "how has today gone". This is the other half — the shift's finished work
// above the line and its queue below it, in the scope the home screen is set
// to, so the two screens always agree about which flowlines are in view.
//
// It promotes the old timeline home mode: same idea, but a place you can go to
// rather than a setting that replaced the home screen for good.
//
// Watching every line at once, the day is one tab per flowline rather than one
// merged list: a station fed by three lines interleaves them by production
// sequence, and an operator standing at it thinks in lines, not in the merge.
//
// Each lane counts 1..n, where n is what this station is asked for on that line
// today — its own target where it has one, the flowline's plan where it does
// not. Finished and still-to-do pods sit in that one list together, in
// production sequence, finished ones marked as such: the question is "where is
// the day up to", a day split into two lists made the operator add them up,
// and grouping the finished ones at the top hid which pod had been skipped.
// Pods past n are here and still tappable, but they are not today's, so they
// wait under a disclosure rather than making the day look endless — and a pod
// signed off from there stays there.
//
// The day itself is a choice. It opens on today, which is the question an
// operator has at the station, and steps either way from there — an arrow per
// day for the near ones, the device's own date picker for the far ones. Only
// half the screen can follow it: what was finished on a day is a fact Odoo
// keeps, and the queue is not. The queue is the pods physically standing here
// now, with no history behind it and no forecast in front, so on any day but
// today it is not shown at all rather than shown as if it were that day's.

import { useEffect, useMemo, useState } from 'react';
import { Banner } from '@/components/ui/Banner';
import { EmptyState, Skeleton } from '@/components/ui/misc';
import { StatChip } from '@/components/home/Score';
import { PodStateChip } from '@/components/pod/PodParts';
import { useLang, useT } from '@/i18n/useT';
import type { Lang, Translations } from '@/i18n/strings';
import { useCompletedOn, useQueue, useScore, useTargets } from '@/queries';
import { localDateKey, shiftDateKey } from '@/lib/pace';
import { type DayRow, doneInDay, flowlineDays, type FlowlineDay } from '@/lib/podDay';
import { useStore } from '@/store';
import type { CompletedPodVM, PodVM } from '@/api/types';

export function PodsScreen({ onOpen }: { onOpen: (pod: PodVM) => void }) {
  const t = useT();
  const wcId = useStore((s) => s.workcentreId);
  const wcName = useStore((s) => s.workcentreName);
  const flowlineId = useStore((s) => s.flowlineId);
  const startedWoId = useStore((s) => s.startedWoId);
  const lane = useStore((s) => s.podsLane);
  const clearLane = useStore((s) => s.clearPodsLane);
  // 'auto' until the operator picks a tab — or arrives with one picked for
  // them, from a line's badge on the home screen — so the screen can keep
  // landing on the line they are working right up until they say otherwise.
  const [picked, setPicked] = useState<number | null | 'auto'>(lane ?? 'auto');
  useEffect(() => {
    if (lane == null) return;
    setPicked(lane);
    // A hint, consumed: the next visit lands by its own rules again.
    clearLane();
  }, [lane, clearLane]);
  const [futureOpen, setFutureOpen] = useState(false);

  // Read once per render rather than held in state: a tablet left on this
  // screen overnight would otherwise still call yesterday "today".
  const today = localDateKey();
  const [day, setDay] = useState(today);
  const isToday = day === today;
  const isFuture = day > today;

  // The score already resolves today's number the way the board does: the
  // station's own target for the line, else the line's plan. Same query, same
  // cache — this screen must not answer that question differently.
  const score = useScore();
  // The plan as it was set for the day being read. On today this is the same
  // rows the score is already holding, so it costs nothing; on another day it
  // is the only way the lanes get a denominator at all.
  const dayTargets = useTargets(day);
  const scope = useMemo(
    () =>
      (score.data?.lines ?? []).map((l) => ({
        id: l.flowlineId,
        name: l.flowlineName,
        short: l.short,
        target: isToday
          ? l.target
          : (dayTargets.data?.workcentre[`${wcId}:${l.flowlineId}`] ??
            dayTargets.data?.flowline[l.flowlineId]?.target ??
            null),
      })),
    [score.data, isToday, dayTargets.data, wcId],
  );

  const queue = useQueue();
  const completed = useCompletedOn(day);
  // The queue has no other day to be. Feeding it into another day's lanes would
  // put pods that are standing here now under a heading that says they were
  // waiting last Tuesday.
  const pods = isToday ? (queue.data ?? []) : [];
  const done = completed.data ?? [];

  // The score is already narrowed to the picked line, exactly as the reads are.
  const scopeWord = useMemo(() => {
    if (flowlineId == null) return scope.length > 1 ? t.allFlowlinesT : (scope[0]?.name ?? '');
    return scope.find((s) => s.id === flowlineId)?.name ?? '';
  }, [flowlineId, scope, t.allFlowlinesT]);

  const days = useMemo(
    () => flowlineDays(pods, done, scope, t.noFlowlineT, { keepVisibleWoId: startedWoId }),
    [pods, done, scope, t.noFlowlineT, startedWoId],
  );

  // One line is not a choice — and with a chip picked there is only ever one.
  const showTabs = days.length > 1;
  const shown = showTabs ? pickDay(days, picked, startedWoId) : (days[0] ?? null);

  const todayRows = shown?.today ?? [];
  const futureRows = shown?.future ?? [];
  const doneCount = shown ? doneInDay(shown) : 0;
  const queuedCount = todayRows.length - doneCount;
  const n = shown?.n ?? todayRows.length;
  const doneToday = todayRows.filter((r): r is Extract<DayRow, { kind: 'done' }> => r.kind === 'done');
  const rft = doneToday.length
    ? Math.round((doneToday.filter((r) => r.pod.rft).length / doneToday.length) * 100)
    : null;
  const showFlowline = !showTabs && flowlineId == null;
  const loading = completed.isPending || (isToday && queue.isPending);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <header>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <h1 style={{ font: "700 18px 'Space Grotesk',sans-serif", margin: 0 }}>
            {isToday ? t.todaysPodsT : t.podsAtStationT}
          </h1>
          <span style={{ font: '500 12px Manrope,sans-serif', color: 'var(--sub)' }}>
            {[wcName, scopeWord, isToday ? `${pods.length} ${t.readyLowerT}` : null].filter(Boolean).join(' · ')}
          </span>
        </div>
        <DayBar day={day} today={today} onPick={setDay} />
      </header>

      {showTabs && (
        <FlowlineTabs
          days={days}
          activeId={shown?.id ?? null}
          onPick={(id) => setPicked(id)}
        />
      )}

      <div>
        {isToday && queue.isError ? (
          <Banner tone="danger" action={{ label: t.retry, onClick: () => queue.refetch() }}>
            {(queue.error as Error).message}
          </Banner>
        ) : null}

        <section aria-label={isToday ? t.dayTodayT : t.finishedT}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
            <span className="sectionLabel" style={{ marginBottom: 0 }}>
              {isToday ? t.dayTodayT : t.finishedT} · {doneCount}/{n} {t.done}
            </span>
            {rft != null && (
              <StatChip tone={rft === 100 ? 'pos' : 'neutral'} size="sm" style={{ marginLeft: 'auto' }}>
                {rft}% {t.rightFirstTimeShortT}
              </StatChip>
            )}
          </div>
          {loading ? (
            <div style={{ display: 'grid', gap: 6 }}>
              <Skeleton height={52} radius={13} />
              <Skeleton height={52} radius={13} />
              <Skeleton height={52} radius={13} />
            </div>
          ) : todayRows.length === 0 ? (
            <div style={{ background: 'var(--surface2)', borderRadius: 13 }}>
              <EmptyState>
                {!isToday
                  ? isFuture
                    ? t.dayNotHappenedYetT
                    : t.nothingFinishedThatDayT
                  : showTabs
                    ? t.nothingOnThisLineT
                    : t.nothingWaitingStationT}
              </EmptyState>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {/* In production sequence, numbered straight through: #1 signed
                  off, #2 still to do, #3 signed off out of turn, #4 in hand.
                  One list, because it is one day, and the gaps are the point. */}
              {todayRows.map((r) =>
                r.kind === 'done' ? (
                  <DoneRow key={`d${r.pod.woId}`} pod={r.pod} position={r.position} />
                ) : (
                  <QueuedRow
                    key={r.pod.woId}
                    pod={r.pod}
                    position={r.position}
                    started={r.pod.woId === startedWoId}
                    showFlowline={showFlowline}
                    onClick={() => onOpen(r.pod)}
                  />
                ),
              )}
              {isToday && queuedCount === 0 && n > 0 && doneCount >= n && (
                <div style={{ font: '500 11.5px Manrope,sans-serif', color: 'var(--pos)', padding: '6px 4px 0' }}>
                  {t.todaysNumberDoneT}
                </div>
              )}
            </div>
          )}

          {futureRows.length > 0 && (
            <FuturePods
              open={futureOpen}
              onToggle={() => setFutureOpen((v) => !v)}
              rows={futureRows}
              showFlowline={showFlowline}
              onOpen={onOpen}
            />
          )}
        </section>
      </div>
    </div>
  );
}

/**
 * The day being read, and the two ways off it.
 *
 * An arrow either side for the day next door, which is the overwhelming case —
 * "what did we get through yesterday", "what is planned for tomorrow" — and the
 * device's own date picker behind the label for anything further out. The
 * picker is the platform's: a wall-mounted tablet already has one an operator
 * knows, and a hand-rolled calendar in a 375px column is worse in every way.
 *
 * The label is the input. A transparent `input[type=date]` sits over the whole
 * button rather than beside it, because `showPicker()` is still refused by
 * enough browsers — and throws where it is refused — that a tap landing on a
 * real date field is the only version that works everywhere.
 *
 * Neither arrow is capped. There is no floor worth enforcing on the past, and
 * a future day that has no plan on it says so; disabling the arrow at some
 * invented horizon would just leave an operator pressing a dead control.
 */
function DayBar({
  day,
  today,
  onPick,
}: {
  day: string;
  today: string;
  onPick: (day: string) => void;
}) {
  const t = useT();
  const lang = useLang();
  const label = dayLabel(day, today, t, lang);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 8 }}>
      <DayStep dir={-1} label={t.previousDayT} onClick={() => onPick(shiftDateKey(day, -1))} />

      <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
        <button
          type="button"
          className="press"
          // The input above it takes the tap; this is here for its looks and
          // for the screen reader, so it must not be a second tab stop.
          tabIndex={-1}
          style={{
            width: '100%',
            minHeight: 44,
            borderRadius: 12,
            background: 'var(--surface2)',
            color: 'var(--ink)',
            font: "700 13px 'Space Grotesk',sans-serif",
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
          }}
        >
          {label}
          <span aria-hidden="true" style={{ font: '600 10px Manrope,sans-serif', color: 'var(--sub)' }}>
            ▾
          </span>
        </button>
        <input
          type="date"
          value={day}
          aria-label={t.pickADayT}
          // Empty is what a picker returns when it is dismissed, or while a
          // typed date is half-entered. Neither is a day to navigate to.
          onChange={(e) => e.target.value && onPick(e.target.value)}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            opacity: 0,
            border: 0,
            padding: 0,
            background: 'transparent',
          }}
        />
      </div>

      <DayStep dir={1} label={t.nextDayT} onClick={() => onPick(shiftDateKey(day, 1))} />

      {day !== today && (
        <button
          type="button"
          className="press"
          onClick={() => onPick(today)}
          style={{
            flex: 'none',
            minHeight: 44,
            padding: '0 10px',
            borderRadius: 12,
            background: 'transparent',
            color: 'var(--accent)',
            font: "700 12px 'Space Grotesk',sans-serif",
          }}
        >
          {t.dayTodayT}
        </button>
      )}
    </div>
  );
}

function DayStep({ dir, label, onClick }: { dir: -1 | 1; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      className="press"
      aria-label={label}
      onClick={onClick}
      style={{
        flex: 'none',
        width: 44,
        minHeight: 44,
        borderRadius: 12,
        background: 'var(--surface2)',
        color: 'var(--ink)',
        font: '600 15px Manrope,sans-serif',
      }}
    >
      <span aria-hidden="true">{dir < 0 ? '‹' : '›'}</span>
    </button>
  );
}

/**
 * "Today", "Yesterday", "Tomorrow", then "Wed 12 Aug".
 *
 * The three named days carry the whole of the near navigation, which is where
 * this control is used from; a date beyond them is written out because "3 days
 * ago" makes an operator count. The key is a plain calendar date, so it is
 * formatted in UTC — the zone that midnight was minted in — rather than the
 * device's, which would slide it a day either way for anyone whose tablet is
 * set wrong.
 */
function dayLabel(day: string, today: string, t: Translations, lang: Lang): string {
  if (day === today) return t.dayTodayT;
  if (day === shiftDateKey(today, -1)) return t.dayYesterdayT;
  if (day === shiftDateKey(today, 1)) return t.dayTomorrowT;
  return new Intl.DateTimeFormat(lang === '中文' ? 'zh-CN' : 'en-AU', {
    timeZone: 'UTC',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(new Date(`${day}T00:00:00Z`));
}

/**
 * Which lane to show: the operator's own pick, else the line they have a pod
 * started on, else the first line with something still to do, else the first.
 * A line that has finished its day is a poor landing place while another is
 * still running.
 */
function pickDay(
  days: FlowlineDay[],
  picked: number | null | 'auto',
  startedWoId: number | null,
): FlowlineDay | null {
  if (picked !== 'auto') {
    const chosen = days.find((d) => d.id === picked);
    if (chosen) return chosen;
  }
  if (startedWoId != null) {
    const working = days.find((d) => d.queued.some((p) => p.woId === startedWoId));
    if (working) return working;
  }
  return (
    days.find((d) => d.today.some((r) => r.kind === 'queued')) ??
    days.find((d) => d.queued.length > 0) ??
    // On a day that is over there is no queue to land on, so the lane that
    // actually ran is the one worth opening.
    days.find((d) => d.done.length > 0) ??
    days[0] ??
    null
  );
}

/**
 * One tab per flowline, each showing how much of its day is behind it.
 *
 * Scrolls rather than squeezes: a station can feed six lines, and six legible
 * tabs one thumb-flick apart beat six unreadable ones that all fit.
 */
function FlowlineTabs({
  days,
  activeId,
  onPick,
}: {
  days: FlowlineDay[];
  activeId: number | null;
  onPick: (id: number | null) => void;
}) {
  const t = useT();
  return (
    <div
      role="tablist"
      aria-label={t.podsByFlowlineT}
      style={{
        flex: 'none',
        display: 'flex',
        gap: 4,
        overflowX: 'auto',
        padding: '4px 0 0',
        borderBottom: '1px solid var(--line)',
      }}
    >
      {days.map((d) => {
        const active = d.id === activeId;
        const finished = doneInDay(d);
        const complete = d.n > 0 && finished >= d.n;
        return (
          <button
            key={d.id ?? 'none'}
            type="button"
            role="tab"
            className="press"
            aria-selected={active}
            aria-label={`${d.name} — ${finished}/${d.n} ${t.done}`}
            onClick={() => onPick(d.id)}
            style={{
              flex: 'none',
              background: 'transparent',
              padding: '6px 10px 7px',
              borderBottom: `2.5px solid ${active ? 'var(--accent)' : 'transparent'}`,
              marginBottom: -1,
              color: active ? 'var(--ink)' : 'var(--sub)',
              minHeight: 44,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              whiteSpace: 'nowrap',
            }}
          >
            <span style={{ font: `${active ? 700 : 600} 12.5px 'Space Grotesk',sans-serif` }}>{d.short}</span>
            <span
              aria-hidden="true"
              style={{
                // Green once the line's day is behind it — the one state worth
                // reading off the strip without opening the tab.
                font: '600 10.5px Manrope,sans-serif',
                color: complete ? 'var(--pos)' : 'var(--sub)',
              }}
            >
              {finished}/{d.n}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * The pods that are here but past today's number.
 *
 * Folded away rather than dropped: they are real work orders sitting at this
 * station, and an operator who runs ahead of the plan — or who needs to see
 * what is coming — must still be able to reach them. Shut by default, because
 * the question this screen answers is how today is going. A pod signed off
 * from in here is listed in here, done: the plan did not grow because someone
 * got ahead of it.
 */
function FuturePods({
  open,
  onToggle,
  rows,
  showFlowline,
  onOpen,
}: {
  open: boolean;
  onToggle: () => void;
  rows: DayRow[];
  showFlowline: boolean;
  onOpen: (pod: PodVM) => void;
}) {
  const t = useT();
  const doneCount = rows.filter((r) => r.kind === 'done').length;
  return (
    <div style={{ marginTop: 10 }}>
      <button
        type="button"
        className="press-row"
        onClick={onToggle}
        aria-expanded={open}
        style={{
          width: '100%',
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          minHeight: 44,
          padding: '10px 13px',
          borderRadius: 13,
          background: 'var(--surface2)',
          color: 'var(--sub)',
        }}
      >
        <span aria-hidden="true" style={{ font: '600 11px Manrope,sans-serif', width: 10 }}>
          {open ? '▾' : '▸'}
        </span>
        <span style={{ flex: 1, minWidth: 0, font: "600 12px 'Space Grotesk',sans-serif" }}>
          {t.futurePodsT} · {rows.length}
          {doneCount > 0 ? ` · ${doneCount} ${t.done}` : ''}
        </span>
        <span style={{ font: '500 10.5px Manrope,sans-serif' }}>{t.beyondTodaysNumberT}</span>
      </button>

      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
          {rows.map((r) =>
            r.kind === 'done' ? (
              <DoneRow key={`d${r.pod.woId}`} pod={r.pod} position={r.position} dim />
            ) : (
              <QueuedRow
                key={r.pod.woId}
                pod={r.pod}
                position={r.position}
                started={false}
                showFlowline={showFlowline}
                dim
                onClick={() => onOpen(r.pod)}
              />
            ),
          )}
        </div>
      )}
    </div>
  );
}

/**
 * A pod that is signed off. Sits in the same list as the ones still to do, so
 * it has to read as finished at a glance: the green tick chip does that — the
 * same chip a queued row wears for its state, so the column reads as one —
 * and the quieter surface keeps the eye on the pods that still need a hand.
 */
function DoneRow({ pod, position, dim = false }: { pod: CompletedPodVM; position: number; dim?: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        background: dim ? 'transparent' : 'var(--surface2)',
        border: '1px solid var(--line)',
        borderRadius: 12,
        padding: '10px 12px',
        minHeight: 44,
        opacity: dim ? 0.62 : 1,
      }}
    >
      <PodStateChip state="done" />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', font: "700 12.5px 'Space Grotesk',sans-serif", color: 'var(--sub)' }}>{pod.mo}</span>
        <span
          style={{
            display: 'block',
            font: '500 10.5px Manrope,sans-serif',
            color: 'var(--sub)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {[pod.proj, pod.type].filter(Boolean).join(' · ')}
        </span>
      </span>
      <span style={{ font: '600 10.5px Manrope,sans-serif', color: 'var(--sub)', flex: 'none' }}>
        {pod.time}
        {pod.rft ? ' · RFT' : ''}
      </span>
      <span style={{ font: '600 10.5px Manrope,sans-serif', color: 'var(--sub)', flex: 'none' }}>#{position}</span>
    </div>
  );
}

function QueuedRow({
  pod,
  position,
  started,
  showFlowline,
  dim = false,
  onClick,
}: {
  pod: PodVM;
  position: number;
  started: boolean;
  showFlowline: boolean;
  /** Past today's number: still a pod, still tappable, but not today's work. */
  dim?: boolean;
  onClick: () => void;
}) {
  const t = useT();
  return (
    <button
      type="button"
      className="press-row"
      onClick={onClick}
      style={{
        width: '100%',
        textAlign: 'left',
        background: dim ? 'transparent' : 'var(--surface)',
        border: `1px solid ${started ? 'var(--accent)' : 'var(--line)'}`,
        borderRadius: 13,
        padding: '11px 13px',
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        minHeight: 52,
        opacity: dim ? 0.62 : 1,
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <span style={{ font: "700 13.5px 'Space Grotesk',sans-serif" }}>{pod.mo}</span>
          {showFlowline && pod.flowlineName && (
            <StatChip tone="neutral" size="sm">
              {pod.flowlineName.replace(/^Flowline\s*/i, 'FL ')}
            </StatChip>
          )}
          {/* Always worn, not only when started: a queued row can be a pod
              that is here to work, one in someone's hands, or one still
              upstream, and the list is where that has to be told apart. */}
          <PodStateChip state={pod.state} started={started} />
        </span>
        <span
          style={{
            display: 'block',
            font: '500 11px Manrope,sans-serif',
            color: 'var(--sub)',
            marginTop: 2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {[pod.proj, pod.type, pod.est ? `${t.estimated} ${pod.est}` : null].filter(Boolean).join(' · ')}
        </span>
      </span>
      <span style={{ font: '600 11px Manrope,sans-serif', color: 'var(--sub)', flex: 'none' }}>#{position}</span>
    </button>
  );
}
